import React, {
  ReactNode,
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
} from "react"

import { ContextStatus } from "../../types/statusTypes"
import { app } from "../../utils/firebase"
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  writeBatch,
  updateDoc,
} from "firebase/firestore"
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage"

import firestoreReducer, {
  initialState,
  State,
  Actions,
} from "./firestoreReducer"
import useAuth from "./authContext"
import {
  BriefDish,
  DetailedDish,
  FirestoreCollection,
  Gin,
  Tonic,
} from "../../types/menuTypes"
import { Promo } from "../../types/promoType"

type ContextProps = {
  children: ReactNode
}

type ContextValue = State & {
  updateDetailedDish: (
    collection: FirestoreCollection,
    dish: DetailedDish
  ) => void
  updateBriefDish: (collection: FirestoreCollection, dish: BriefDish) => void
  updateGin: (collection: FirestoreCollection, dish: Gin) => void
  updateTonic: (collection: FirestoreCollection, dish: Tonic) => void
  updatePositions: (collection: FirestoreCollection, orderedList: any[]) => void
  deleteDish: (
    collection: FirestoreCollection,
    id: string,
    imageExists: boolean
  ) => void
  getMenuItems: (collection: FirestoreCollection) => any[]
  deleteDone: boolean
  setDeleteDone: React.Dispatch<React.SetStateAction<boolean>>
  updateDone: boolean
  setUpdateDone: React.Dispatch<React.SetStateAction<boolean>>
  orderItemsForGrid: (items: any[]) => any[]
  getPromos: () => void
  updatePromo: (promo: Promo) => void
  updateActiveStatus: (promo: Promo) => void
  deletePromo: (promo: Promo) => void
}

const FirestoreContext = createContext<ContextValue | undefined>(undefined)

export function FirestoreProvider({ children }: ContextProps) {
  const [state, dispatch] = useReducer(firestoreReducer, initialState)
  const [updateDone, setUpdateDone] = useState(false)
  const [deleteDone, setDeleteDone] = useState(false)
  const { currentUser } = useAuth()

  if (!app) return null

  useEffect(() => {
    getPromos()
    getMenu()
    if (currentUser) getMenuCollections()
  }, [currentUser])

  const db = getFirestore(app)
  const storage = getStorage(app)

  // MARK : DISHES - READ FROM FIRESTORE
  const getMenu = async () => {
    setStatus({
      state: "pending",
    })

    try {
      // Entrées 🫒
      const entreesSnapshot = await getDocs(
        collection(db, FirestoreCollection.entrees)
      )
      let entrees: DetailedDish[] = []
      entreesSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const entree = {
          id: doc.id,
          title: data.title,
          ingredients: data.ingredients,
          price: formatPrice(data.price),
          option: data.option,
          position: data.position,
          image: data.image,
          description: data.description,
          foodRestrictions: data.foodRestrictions,
          moreLink: data.moreLink,
        } as DetailedDish
        entrees.push(entree)
      })
      entrees.sort(compareByPosition)

      // Plats 🍔
      const platsSnapshot = await getDocs(
        collection(db, FirestoreCollection.plats)
      )
      let plats: DetailedDish[] = []
      platsSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const plat = {
          id: doc.id,
          title: data.title,
          ingredients: data.ingredients,
          price: formatPrice(data.price),
          option: data.option,
          position: data.position,
          image: data.image,
          description: data.description,
          foodRestrictions: data.foodRestrictions,
          moreLink: data.moreLink,
        } as DetailedDish
        plats.push(plat)
      })
      plats.sort(compareByPosition)

      // Dessert 🍰
      const dessertsSnapshot = await getDocs(
        collection(db, FirestoreCollection.desserts)
      )
      let desserts: BriefDish[] = []
      dessertsSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const dessert = {
          id: doc.id,
          title: data.title,
          price: formatPrice(data.price),
          position: data.position,
          image: data.image,
          description: data.description,
          foodRestrictions: data.foodRestrictions,
          moreLink: data.moreLink,
        } as BriefDish
        desserts.push(dessert)
      })
      desserts.sort(compareByPosition)

      // Fûts 🍺
      const futsSnapshot = await getDocs(
        collection(db, FirestoreCollection.futs)
      )
      let futs: BriefDish[] = []
      futsSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const fut = {
          id: doc.id,
          title: data.title,
          price: formatPrice(data.price),
          position: data.position,
          image: data.image,
          description: data.description,
          moreLink: data.moreLink,
        } as BriefDish
        futs.push(fut)
      })
      futs.sort(compareByPosition)

      // Bouteilles 🍾
      const bouteillesSnapshot = await getDocs(
        collection(db, FirestoreCollection.bouteilles)
      )
      let bouteilles: BriefDish[] = []
      bouteillesSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const bouteille = {
          id: doc.id,
          title: data.title,
          price: formatPrice(data.price),
          position: data.position,
          image: data.image,
          description: data.description,
          moreLink: data.moreLink,
        } as BriefDish
        bouteilles.push(bouteille)
      })
      bouteilles.sort(compareByPosition)

      //  Vins Rouges 🍷
      const vinsRougesSnapshot = await getDocs(
        collection(db, FirestoreCollection.vinsRouges)
      )
      let vinsRouges: BriefDish[] = []
      vinsRougesSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const vin = {
          id: doc.id,
          title: data.title,
          price: formatPrice(data.price),
          position: data.position,
          image: data.image,
          description: data.description,
          moreLink: data.moreLink,
        } as BriefDish
        vinsRouges.push(vin)
      })
      vinsRouges.sort(compareByPosition)

      // Vins Blancs 🍷
      const vinsBlancsSnapshot = await getDocs(
        collection(db, FirestoreCollection.vinsBlancs)
      )
      let vinsBlancs: BriefDish[] = []
      vinsBlancsSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const vin = {
          id: doc.id,
          title: data.title,
          price: formatPrice(data.price),
          position: data.position,
          image: data.image,
          description: data.description,
          moreLink: data.moreLink,
        } as BriefDish
        vinsBlancs.push(vin)
      })
      vinsBlancs.sort(compareByPosition)

      // Cocktails 🍹
      const cocktailsSnapshot = await getDocs(
        collection(db, FirestoreCollection.cocktails)
      )
      let cocktails: DetailedDish[] = []
      cocktailsSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const cocktail = {
          id: doc.id,
          title: data.title,
          ingredients: data.ingredients,
          price: formatPrice(data.price),
          option: data.option,
          position: data.position,
          image: data.image,
          description: data.description,
          moreLink: data.moreLink,
        } as DetailedDish
        cocktails.push(cocktail)
      })
      cocktails.sort(compareByPosition)

      // Gins 🍹
      const ginsSnapshot = await getDocs(
        collection(db, FirestoreCollection.gins)
      )
      let gins: Gin[] = []
      ginsSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const gin = {
          id: doc.id,
          title: data.title,
          origin: data.origin,
          ingredients: data.ingredients,
          suggestion: data.suggestion,
          price: formatPrice(data.price),
          option: data.option,
          position: data.position,
          image: data.image,
          description: data.description,
          moreLink: data.moreLink,
          flavors: data.flavors,
        } as Gin
        gins.push(gin)
      })
      gins.sort(compareByPosition)

      // Tonics 🥃
      const tonicsSnapshot = await getDocs(
        collection(db, FirestoreCollection.tonics)
      )
      let tonics: Tonic[] = []
      tonicsSnapshot.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const tonic = {
          id: doc.id,
          title: data.title,
          ingredients: data.ingredients,
          price: formatPrice(data.price),
          position: data.position,
          image: data.image,
          description: data.description,
          moreLink: data.moreLink,
        } as Tonic
        tonics.push(tonic)
      })
      tonics.sort(compareByPosition)

      dispatch({
        type: Actions.setMenu,
        payload: {
          entrees,
          plats,
          desserts,
          futs,
          bouteilles,
          vinsRouges,
          vinsBlancs,
          cocktails,
          gins,
          tonics,
        },
      })
      setStatus({
        state: "resolved",
        message: "Successfully retrieved menu",
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  // MARK: DISHES - WRITE TO FIRESTORE
  const updateDetailedDish = async (
    collection: FirestoreCollection,
    dish: DetailedDish
  ) => {
    setStatus({
      state: "pending",
    })
    setUpdateDone(false)
    try {
      if (!currentUser)
        throw new Error(
          "Vous devez être connecté pour modifier un item du menu."
        )

      let priceNumber = parseFloat(dish.price ?? "")
      if (dish.price !== "" && isNaN(priceNumber))
        throw new Error("Le prix doit être un nombre.")

      let originalDish: DetailedDish | undefined
      switch (collection) {
        case FirestoreCollection.entrees:
          let entrees = state.entrees
          originalDish = state.entrees.find(i => i.id == dish.id)

          if (!originalDish) {
            dish.position = !isNaN(entrees[entrees.length - 1]?.position)
              ? entrees[entrees.length - 1]?.position + 1
              : entrees.length + 1
            entrees.push(dish)
          } else {
            let newPosition = entrees.length + 1
            dish.position = !isNaN(originalDish.position)
              ? originalDish.position
              : newPosition

            let matchIndex = entrees.length
            entrees = entrees.filter((i, index) => {
              if (i.id == dish.id) {
                matchIndex = index
              }
              return i.id !== dish.id
            })
            entrees.splice(matchIndex, 0, dish)
          }

          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              entrees,
            },
          })
          break
        case FirestoreCollection.plats:
          let plats = state.plats
          originalDish = state.plats.find(i => i.id == dish.id)

          if (!originalDish) {
            dish.position = !isNaN(plats[plats.length - 1]?.position)
              ? plats[plats.length - 1]?.position + 1
              : plats.length + 1
            plats.push(dish)
          } else {
            let newPosition = plats.length + 1
            dish.position = !isNaN(originalDish.position)
              ? originalDish.position
              : newPosition
            let matchIndex = plats.length
            plats = plats.filter((i, index) => {
              if (i.id == dish.id) {
                matchIndex = index
              }
              return i.id !== dish.id
            })
            plats.splice(matchIndex, 0, dish)
          }

          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              plats,
            },
          })
          break
        case FirestoreCollection.cocktails:
          let cocktails = state.cocktails
          originalDish = state.cocktails.find(i => i.id == dish.id)

          if (!originalDish) {
            dish.position = !isNaN(cocktails[cocktails.length - 1]?.position)
              ? cocktails[cocktails.length - 1]?.position + 1
              : cocktails.length + 1
            cocktails.push(dish)
          } else {
            let newPosition = cocktails.length + 1
            dish.position = !isNaN(originalDish.position)
              ? originalDish.position
              : newPosition

            let matchIndex = cocktails.length
            cocktails = cocktails.filter((i, index) => {
              if (i.id == dish.id) {
                matchIndex = index
              }
              return i.id !== dish.id
            })
            cocktails.splice(matchIndex, 0, dish)
          }

          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              cocktails,
            },
          })
          break
      }

      let sanitizedDish = {
        ...dish,
        foodRestrictions: [...new Set(dish.foodRestrictions)],
      } as any
      let imageUrl = ""

      if (dish.price !== "") {
        sanitizedDish["price"] = priceNumber
      }

      if (dish.image && originalDish?.image !== dish.image) {
        imageUrl = await uploadImage(collection, dish.image, dish.id)
        sanitizedDish.image = imageUrl
      }

      sanitizedDish = Object.fromEntries(
        Object.entries(sanitizedDish)
          .filter(([_, v]) => v != null)
          .filter(([_, v]) => v != "")
      )
      const docRef = doc(db, collection, dish.id)
      await setDoc(docRef, sanitizedDish, { merge: true })
      setUpdateDone(true)
      alert(`Item ${originalDish ? "modifié" : "ajouté"} avec succès !`)
      setStatus({
        state: "resolved",
        message: `Successfully updated ${collection}`,
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  const updateBriefDish = async (
    collection: FirestoreCollection,
    dish: BriefDish
  ) => {
    setStatus({
      state: "pending",
    })
    setUpdateDone(false)

    try {
      if (!currentUser)
        throw new Error(
          "Vous devez être connecté pour modifier un item du menu."
        )

      let priceNumber = parseFloat(dish.price ?? "")
      if (isNaN(priceNumber)) throw new Error("Le prix doit être un nombre.")

      let originalDish: BriefDish | undefined
      switch (collection) {
        case FirestoreCollection.desserts:
          let desserts = state.desserts
          originalDish = state.desserts.find(i => i.id == dish.id)
          if (!originalDish) {
            dish.position = !isNaN(desserts[desserts.length - 1]?.position)
              ? desserts[desserts.length - 1]?.position + 1
              : desserts.length + 1
            desserts.push(dish)
          } else {
            let newPosition = desserts.length + 1
            dish.position = !isNaN(originalDish.position)
              ? originalDish.position
              : newPosition

            let matchIndex = desserts.length
            desserts = desserts.filter((i, index) => {
              if (i.id == dish.id) {
                matchIndex = index
              }
              return i.id !== dish.id
            })
            desserts.splice(matchIndex, 0, dish)
          }

          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              desserts,
            },
          })
          break
        case FirestoreCollection.futs:
          let futs = state.futs
          originalDish = state.futs.find(i => i.id == dish.id)

          if (!originalDish) {
            dish.position = !isNaN(futs[futs.length - 1]?.position)
              ? futs[futs.length - 1]?.position + 1
              : futs.length + 1
            futs.push(dish)
          } else {
            let newPosition = futs.length + 1
            dish.position = !isNaN(originalDish.position)
              ? originalDish.position
              : newPosition

            let matchIndex = futs.length
            futs = futs.filter((i, index) => {
              if (i.id == dish.id) {
                matchIndex = index
              }
              return i.id !== dish.id
            })
            futs.splice(matchIndex, 0, dish)
          }

          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              futs,
            },
          })
          break
        case FirestoreCollection.bouteilles:
          let bouteilles = state.bouteilles
          originalDish = state.bouteilles.find(i => i.id == dish.id)
          if (!originalDish) {
            dish.position = !isNaN(bouteilles[bouteilles.length - 1]?.position)
              ? bouteilles[bouteilles.length - 1]?.position + 1
              : bouteilles.length + 1
            bouteilles.push(dish)
          } else {
            let newPosition = bouteilles.length + 1
            dish.position = !isNaN(originalDish.position)
              ? originalDish.position
              : newPosition

            let matchIndex = bouteilles.length
            bouteilles = bouteilles.filter((i, index) => {
              if (i.id == dish.id) {
                matchIndex = index
              }
              return i.id !== dish.id
            })
            bouteilles.splice(matchIndex, 0, dish)
          }

          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              bouteilles,
            },
          })
          break
        case FirestoreCollection.vinsRouges:
          let vinsRouges = state.vinsRouges
          originalDish = state.vinsRouges.find(i => i.id == dish.id)

          if (!originalDish) {
            dish.position = !isNaN(vinsRouges[vinsRouges.length - 1]?.position)
              ? vinsRouges[vinsRouges.length - 1]?.position + 1
              : vinsRouges.length + 1
            vinsRouges.push(dish)
          } else {
            let newPosition = vinsRouges.length + 1
            dish.position = !isNaN(originalDish.position)
              ? originalDish.position
              : newPosition

            let matchIndex = vinsRouges.length
            vinsRouges = vinsRouges.filter((i, index) => {
              if (i.id == dish.id) {
                matchIndex = index
              }
              return i.id !== dish.id
            })
            vinsRouges.splice(matchIndex, 0, dish)
          }

          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              vinsRouges,
            },
          })
          break
        case FirestoreCollection.vinsBlancs:
          let vinsBlancs = state.vinsBlancs
          originalDish = state.vinsBlancs.find(i => i.id == dish.id)

          if (!originalDish) {
            dish.position = !isNaN(vinsBlancs[vinsBlancs.length - 1]?.position)
              ? vinsBlancs[vinsBlancs.length - 1]?.position + 1
              : vinsBlancs.length + 1
            vinsBlancs.push(dish)
          } else {
            let newPosition = vinsBlancs.length + 1
            dish.position = !isNaN(originalDish.position)
              ? originalDish.position
              : newPosition

            let matchIndex = vinsBlancs.length
            vinsBlancs = vinsBlancs.filter((i, index) => {
              if (i.id == dish.id) {
                matchIndex = index
              }
              return i.id !== dish.id
            })
            vinsBlancs.splice(matchIndex, 0, dish)
          }

          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              vinsBlancs,
            },
          })
          break
      }

      let sanitizedDish = {
        ...dish,
        price: priceNumber,
        foodRestrictions: [...new Set(dish.foodRestrictions)],
      } as any

      let imageUrl = ""
      if (dish.image && originalDish?.image !== dish.image) {
        imageUrl = await uploadImage(collection, dish.image, dish.id)
        sanitizedDish.image = imageUrl
      }

      sanitizedDish = Object.fromEntries(
        Object.entries(sanitizedDish)
          .filter(([_, v]) => v != null)
          .filter(([_, v]) => v != "")
      )

      const docRef = doc(db, collection, dish.id)
      await setDoc(docRef, sanitizedDish, { merge: true })
      alert(`Item ${originalDish ? "modifié" : "ajouté"} avec succès !`)

      setStatus({
        state: "resolved",
        message: `Successfully updated ${collection}`,
      })
      setUpdateDone(true)
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  const updateGin = async (collection: FirestoreCollection, gin: Gin) => {
    setStatus({
      state: "pending",
    })
    setUpdateDone(false)

    try {
      if (!currentUser)
        throw new Error(
          "Vous devez être connecté pour modifier un item du menu."
        )

      let priceNumber = parseFloat(gin.price ?? "")
      if (isNaN(priceNumber)) throw new Error("Le prix doit être un nombre.")

      let gins = state.gins
      const originalGin = state.gins.find(i => i.id == gin.id)
      if (!originalGin) {
        gins.push(gin)
      } else {
        let matchIndex = gins.length
        gins = gins.filter((i, index) => {
          if (i.id == gin.id) {
            matchIndex = index
          }
          return i.id !== gin.id
        })
        gins.splice(matchIndex, 0, gin)
      }

      dispatch({
        type: Actions.setMenu,
        payload: {
          ...state,
          gins,
        },
      })

      let sanitizedGin = {
        ...gin,
        price: priceNumber,
      } as any

      let imageUrl = ""
      if (gin.image && originalGin?.image !== gin.image) {
        imageUrl = await uploadImage(collection, gin.image, gin.id)
        sanitizedGin.image = imageUrl
      }

      if (!originalGin || (originalGin && isNaN(originalGin.position))) {
        let newPosition = gins.length
        if (!isNaN(gins[gins.length - 1]?.position)) {
          newPosition = gins[gins.length - 1]?.position
        }
        sanitizedGin.position = newPosition + 1
      } else {
        sanitizedGin.position = gin.position
      }

      sanitizedGin = Object.fromEntries(
        Object.entries(sanitizedGin)
          .filter(([_, v]) => v != null)
          .filter(([_, v]) => v != "")
      )
      sanitizedGin.flavors = [...new Set(gin.flavors)]

      const docRef = doc(db, collection, sanitizedGin.id)
      await setDoc(docRef, sanitizedGin, { merge: true })
      alert(`Gin ${originalGin ? "modifié" : "ajouté"} avec succès !`)

      setStatus({
        state: "resolved",
        message: `Successfully updated ${collection}`,
      })
      setUpdateDone(true)
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  const updateTonic = async (collection: FirestoreCollection, tonic: Tonic) => {
    setStatus({
      state: "pending",
    })
    setUpdateDone(false)

    try {
      if (!currentUser)
        throw new Error(
          "Vous devez être connecté pour modifier un item du menu."
        )

      let priceNumber = parseFloat(tonic.price ?? "")
      if (isNaN(priceNumber)) throw new Error("Le prix doit être un nombre.")

      let tonics = state.tonics
      const originalTonic = state.tonics.find(i => i.id == tonic.id)
      if (!originalTonic) {
        tonics.push(tonic)
      } else {
        let matchIndex = tonics.length
        tonics = tonics.filter((i, index) => {
          if (i.id == tonic.id) {
            matchIndex = index
          }
          return i.id !== tonic.id
        })
        tonics.splice(matchIndex, 0, tonic)
      }

      dispatch({
        type: Actions.setMenu,
        payload: {
          ...state,
          tonics,
        },
      })

      let sanitizedTonic = {
        ...tonic,
        price: priceNumber,
      } as any

      let imageUrl = ""
      if (tonic.image && originalTonic?.image !== tonic.image) {
        imageUrl = await uploadImage(collection, tonic.image, tonic.id)
        sanitizedTonic.image = imageUrl
      }

      if (!originalTonic) {
        sanitizedTonic.position = tonics[tonics.length - 1]?.position + 1
      }

      if (!originalTonic || (originalTonic && isNaN(originalTonic.position))) {
        let newPosition = tonics.length
        if (!isNaN(tonics[tonics.length - 1]?.position)) {
          newPosition = tonics[tonics.length - 1]?.position
        }
        sanitizedTonic.position = newPosition + 1
      } else {
        sanitizedTonic.position = tonic.position
      }

      sanitizedTonic = Object.fromEntries(
        Object.entries(sanitizedTonic)
          .filter(([_, v]) => v != null)
          .filter(([_, v]) => v != "")
      )

      const docRef = doc(db, collection, sanitizedTonic.id)
      await setDoc(docRef, sanitizedTonic, { merge: true })
      alert(`Tonic ${originalTonic ? "modifié" : "ajouté"} avec succès !`)

      setStatus({
        state: "resolved",
        message: `Successfully updated ${collection}`,
      })
      setUpdateDone(true)
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  const updatePositions = async (
    collection: FirestoreCollection,
    orderedList: any[]
  ) => {
    setStatus({
      state: "pending",
    })
    try {
      if (!currentUser)
        throw new Error(
          "Vous devez être connecté pour réordonner les items du menu."
        )

      const indexOfCollection =
        Object.values(FirestoreCollection).indexOf(collection)

      const key = Object.keys(FirestoreCollection)[
        indexOfCollection
      ] as keyof typeof FirestoreCollection
      const originalList = state[key]

      console.log({
        [key]: orderedList,
      })

      dispatch({
        type: Actions.setMenu,
        payload: {
          ...state,
          [key]: orderedList,
        },
      })

      const batch = writeBatch(db)

      orderedList.forEach((item, index) => {
        if (!item.id) return
        const ref = doc(db, collection, item.id)
        if (originalList[index].id !== item.id) {
          batch.update(ref, { position: index })
        }
      })
      await batch.commit()
      alert("Liste mise à jour!")
      setStatus({
        state: "resolved",
        message: `Successfully updated ${collection}`,
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  const uploadImage = async (
    collection: FirestoreCollection | "Promos",
    fileUrl: string,
    dishName: string
  ) => {
    try {
      let file = await fetch(fileUrl)
      let blob = await file.blob()

      const storageRef = ref(storage, `${collection}/${dishName}.jpeg`)
      const metadata = {
        contentType: "image/jpeg",
      }
      await uploadBytes(storageRef, blob, metadata)
      let url = await getDownloadURL(storageRef)
      setStatus({
        state: "resolved",
        message: `Successfully uploaded image to Firebase Storage!`,
      })
      return url
    } catch (error) {
      throw error
    }
  }

  // MARK: DISHES - DELETE FROM FIRESTORE
  const deleteDish = async (
    collection: FirestoreCollection,
    id: string,
    imageExists: boolean
  ) => {
    try {
      switch (collection) {
        case FirestoreCollection.bouteilles:
          const bouteilles = state.bouteilles.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              bouteilles,
            },
          })
          break
        case FirestoreCollection.cocktails:
          const cocktails = state.cocktails.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              cocktails,
            },
          })
          break
        case FirestoreCollection.desserts:
          const desserts = state.desserts.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              desserts,
            },
          })
          break
        case FirestoreCollection.entrees:
          const entrees = state.entrees.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              entrees,
            },
          })
          break
        case FirestoreCollection.futs:
          const futs = state.futs.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              futs,
            },
          })
          break
        case FirestoreCollection.vinsRouges:
          const vinsRouges = state.vinsRouges.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              vinsRouges,
            },
          })
          break
        case FirestoreCollection.vinsBlancs:
          const vinsBlancs = state.vinsBlancs.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              vinsBlancs,
            },
          })
          break
        case FirestoreCollection.plats:
          const plats = state.plats.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              plats,
            },
          })
          break
        case FirestoreCollection.gins:
          const gins = state.gins.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              gins,
            },
          })
          break
        case FirestoreCollection.tonics:
          const tonics = state.tonics.filter(i => i.id !== id)
          dispatch({
            type: Actions.setMenu,
            payload: {
              ...state,
              tonics,
            },
          })
          break
      }

      await deleteDoc(doc(db, collection, id))

      if (imageExists) {
        const imageRef = ref(storage, `${collection}/${id}.jpeg`)
        await deleteObject(imageRef)
      }

      setStatus({
        state: "resolved",
        message: `Successfully deleted dish with id ${id}`,
      })
      alert("Item supprimé du menu !")
      setDeleteDone(true)
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  // MARK : PROMOS - READ FROM FIRESTORE
  const getPromos = async () => {
    setStatus({
      state: "pending",
    })

    try {
      const snapshots = await getDocs(collection(db, "Promos"))

      let promotions: Promo[] = []
      snapshots.forEach(doc => {
        const data = doc.data()
        if (!data) return
        const promo = {
          id: data.id,
          title: data.title,
          description: data.description,
          image: data.image ?? undefined,
          isActive: data.isActive ?? false,
        } as Promo
        promotions.push(promo)
      })
      promotions.sort(compareByTitle)

      dispatch({
        type: Actions.setPromotions,
        payload: {
          promotions,
        },
      })

      dispatch({
        type: Actions.setActivePromotions,
        payload: {
          activePromotions: promotions.filter(i => i.isActive),
        },
      })

      setStatus({
        state: "resolved",
        message: "Successfully retrieved promotions",
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  // MARK : PROMOS - UPDATE IN FIRESTORE
  const updatePromo = async (promo: Promo) => {
    setStatus({
      state: "pending",
    })

    try {
      if (!currentUser)
        throw new Error("Vous devez être connecté pour modifier une promotion.")

      let promotions = state.promotions
      const originalPromo = state.promotions.find(i => i.id == promo.id)
      if (!originalPromo) {
        promotions.push(promo)
      } else {
        promotions = promotions.filter(i => i.id !== promo.id)
        const promoToPush = {
          ...promo,
          isActive: promo.isActive,
        }
        promotions.push(promoToPush)
      }

      dispatch({
        type: Actions.setPromotions,
        payload: {
          ...state,
          promotions: promotions.sort(compareByTitle),
        },
      })

      dispatch({
        type: Actions.setActivePromotions,
        payload: {
          activePromotions: promotions.filter(i => i.isActive),
        },
      })

      let sanitizedPromo = {
        ...promo,
      } as any

      if (promo.image && originalPromo?.image !== promo.image) {
        const imageUrl = await uploadImage("Promos", promo.image, promo.id)
        sanitizedPromo.image = imageUrl
      }

      sanitizedPromo = Object.fromEntries(
        Object.entries(sanitizedPromo)
          .filter(([_, v]) => v != null)
          .filter(([_, v]) => v != "")
      )

      sanitizedPromo["isActive"] = promo.isActive

      const docRef = doc(db, "Promos", sanitizedPromo.id)
      await setDoc(docRef, sanitizedPromo, { merge: true })
      setUpdateDone(true)

      setStatus({
        state: "resolved",
        message: `Successfully updated ${collection}`,
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  const updateActiveStatus = async (promo: Promo) => {
    setStatus({
      state: "pending",
    })

    try {
      if (!currentUser)
        throw new Error(
          "Vous devez être connecté pour modifier le statut actif d'une promotion."
        )

      const updatedPromos = state.promotions.map(i => {
        if (i.id == promo.id) {
          i.isActive = !promo.isActive
        }
        return i
      })

      dispatch({
        type: Actions.setPromotions,
        payload: {
          ...state,
          promotions: updatedPromos.sort(compareByTitle),
        },
      })

      dispatch({
        type: Actions.setActivePromotions,
        payload: {
          activePromotions: updatedPromos.filter(i => i.isActive),
        },
      })

      const docRef = doc(db, "Promos", promo.id)
      await updateDoc(docRef, {
        isActive: promo.isActive,
      })

      setStatus({
        state: "resolved",
        message: `Successfully updated ${collection}`,
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  // MARK : PROMOS - DELETE FROM FIRESTORE
  const deletePromo = async (promo: Promo) => {
    setStatus({
      state: "pending",
    })

    try {
      if (!currentUser)
        throw new Error(
          "Vous devez être connecté pour supprimer une promotion."
        )

      const promotions = state.promotions.filter(i => i.id !== promo.id)
      dispatch({
        type: Actions.setPromotions,
        payload: {
          ...state,
          promotions,
        },
      })

      await deleteDoc(doc(db, "Promos", promo.id))

      const imageExists =
        promo.image != undefined && promo.image != null && promo.image != ""

      if (imageExists) {
        const imageRef = ref(storage, `Promos/${promo.id}.jpeg`)
        await deleteObject(imageRef)
      }

      setStatus({
        state: "resolved",
        message: `Successfully deleted promotion with id ${promo.id}`,
      })
    } catch (error) {
      setStatus({
        state: "rejected",
        error: (error as Error).message,
      })
    }
  }

  // MARK : GET COLLECTIONS
  const getMenuCollections = () => {
    let menuCollections: string[] = []
    for (const key in FirestoreCollection) {
      menuCollections.push(
        FirestoreCollection[key as keyof typeof FirestoreCollection]
      )
    }
    dispatch({
      type: Actions.setMenuCollections,
      payload: {
        ...state,
        menuCollections,
      },
    })
  }

  const getMenuItems = (collection: FirestoreCollection) => {
    switch (collection) {
      case FirestoreCollection.entrees:
        return state.entrees
      case FirestoreCollection.plats:
        return state.plats
      case FirestoreCollection.desserts:
        return state.desserts
      case FirestoreCollection.futs:
        return state.futs
      case FirestoreCollection.bouteilles:
        return state.bouteilles
      case FirestoreCollection.vinsRouges:
        return state.vinsRouges
      case FirestoreCollection.vinsBlancs:
        return state.vinsBlancs
      case FirestoreCollection.cocktails:
        return state.cocktails
      case FirestoreCollection.gins:
        return state.gins
      case FirestoreCollection.tonics:
        return state.tonics
    }
  }

  // MARK: FORMATTING FUNCTIONS
  const formatPrice = (price: number) => {
    if (!price) return ""
    let formattedPrice = `${price}`
    if (price - Math.trunc(price) !== 0.0) {
      formattedPrice = price.toFixed(2)
    }
    return formattedPrice
  }

  const compareByPosition = (a: any, b: any) => {
    if (a.position < b.position) {
      return -1
    }
    if (a.position > b.position) {
      return 1
    }
    return 0
  }

  const compareByTitle = (a: any, b: any) => {
    if (a.title < b.title) {
      return -1
    }
    if (a.title > b.title) {
      return 1
    }
    return 0
  }

  const orderItemsForGrid = (items: any[]) => {
    const halfNumber = Math.ceil(items.length / 2)

    const firstHalf = items.slice(0, halfNumber)
    let secondHalf = items.slice(halfNumber, items.length)

    let updatedArray = []
    for (let i = 0; i < firstHalf.length; i++) {
      updatedArray.push(firstHalf[i])

      if (secondHalf[i]) {
        updatedArray.push(secondHalf[i])
      }
    }

    return [...new Set(updatedArray)]
  }

  const setStatus = (status: ContextStatus) => {
    const { state, message, error, showModal } = status

    // if (state === "resolved") {
    //   console.log("message", message)
    // }

    if (state === "rejected") {
      console.log("error", error)
      alert(error)
    }

    dispatch({
      type: Actions.setStatus,
      payload: {
        status: {
          state,
          message: message || null,
          error: error || null,
          isPending: state === "pending",
          isResolved: state === "resolved",
          isRejected: state === "rejected",
          showModal: showModal || false,
        },
      },
    })
  }

  const value = {
    status: state.status,
    promotions: state.promotions ?? [],
    activePromotions: state.activePromotions ?? [],
    entrees: state.entrees ?? [],
    plats: state.plats ?? [],
    desserts: state.desserts ?? [],
    futs: state.futs ?? [],
    bouteilles: state.bouteilles ?? [],
    vinsRouges: state.vinsRouges ?? [],
    vinsBlancs: state.vinsBlancs ?? [],
    cocktails: state.cocktails ?? [],
    gins: state.gins ?? [],
    tonics: state.tonics ?? [],
    menuCollections: state.menuCollections ?? [],
    updateDetailedDish,
    updateBriefDish,
    updateGin,
    updateTonic,
    updatePositions,
    deleteDish,
    getMenuItems,
    updateDone,
    setUpdateDone,
    deleteDone,
    setDeleteDone,
    orderItemsForGrid,
    getPromos,
    updatePromo,
    updateActiveStatus,
    deletePromo,
  }

  return (
    <FirestoreContext.Provider value={value}>
      {children}
    </FirestoreContext.Provider>
  )
}

const useFirestore = () => {
  const context = useContext(FirestoreContext)

  if (context === undefined) {
    throw new Error("useFirestore must be used within an FirestoreContext")
  }

  return context
}

export default useFirestore
